import styles from '../ContentView.module.css'

import cn from 'clsx'
import React, { useLayoutEffect, useRef, useState } from 'react'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'

import { FaPause } from '@react-icons/all-files/fa/FaPause'
import { FaPlay } from '@react-icons/all-files/fa/FaPlay'
import { LuCircleCheck } from '@react-icons/all-files/lu/LuCircleCheck'
import { LuExternalLink } from '@react-icons/all-files/lu/LuExternalLink'
import { LuHeart } from '@react-icons/all-files/lu/LuHeart'
import { LuLock } from '@react-icons/all-files/lu/LuLock'
// TODO: Temporarily hidden - LuMail used by Contact catalog via email button
// import { LuMail } from '@react-icons/all-files/lu/LuMail'
import { LuMusic } from '@react-icons/all-files/lu/LuMusic'
import { Button } from 'components/ds'
import DescriptionModal from './description-modal'
// TODO: Temporarily hidden - Logo used by Save to DISCO button
// import Logo from 'components/ui/logo'

import type { ContentType } from '../../utils'
import { getAccessButtonLabel, getAccessAriaLabel } from '../../utils'
import { sendDiscoverMixpanelEvent } from '@disco/utils/mixpanel/discover-page'

interface ContentHeroProps {
  contentType: ContentType
  contentId: number
  displayTitle: string
  displayImage: string | null | undefined
  displayDescription: string | undefined
  displayCuratedBy: string | undefined
  isLoadingTracks: boolean
  isCurrentlyPlaying: boolean
  onPlayAll: () => void
  tracksCount: number
  // Catalog-specific props
  isFavorite?: boolean
  hasAccess?: boolean
  catalogStatus?: ClientLibraryDiscoveryPanelItem['status']
  catalogUrl?: string
  catalogContactDetails?: string | null
  onFavoriteClick?: () => void
  onRequestAccess?: () => void
  onContactClick?: () => void
  // Playlist-specific props
  // TODO: Temporarily hidden - Save to DISCO button
  // onSaveToDisco?: () => void
}

const ContentHero = ({
  contentType,
  displayTitle,
  displayImage,
  displayDescription,
  displayCuratedBy,
  isLoadingTracks,
  isCurrentlyPlaying,
  onPlayAll,
  tracksCount,
  isFavorite = false,
  hasAccess = false,
  catalogStatus,
  catalogUrl,
  catalogContactDetails,
  onFavoriteClick,
  onRequestAccess,
  contentId,
}: ContentHeroProps): React.ReactNode => {
  const isAccessDisabled =
    hasAccess || catalogStatus === 'pending' || catalogStatus === 'invited'
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false)
  const [isClamped, setIsClamped] = useState(false)
  const descriptionRef = useRef<HTMLParagraphElement>(null)

  useLayoutEffect(() => {
    const el = descriptionRef.current
    if (!el) return
    const check = () => {
      const clamped = el.scrollHeight > el.clientHeight
      setIsClamped(clamped)
      if (!clamped) setIsDescriptionOpen(false)
    }
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [displayDescription])

  return (
    <div className={styles.playlistHero}>
      <div
        className={cn(
          styles.heroImageContainer,
          contentType === 'playlist' && styles.heroImageContainerWide,
        )}
      >
        {displayImage ? (
          <img
            src={displayImage}
            alt={displayTitle}
            className={styles.heroImage}
          />
        ) : (
          <div className={styles.heroImagePlaceholder}>
            <LuMusic size={48} />
          </div>
        )}
      </div>
      <div className={styles.heroContent}>
        <div>
          <h1 className={styles.playlistTitle} translate="no">
            {displayTitle}
          </h1>

          {displayCuratedBy && (
            <span
              translate="no"
              className={cn(styles.heroBusinessName, {
                [styles.heroBusinessNamePlaylist]: contentType === 'playlist',
              })}
            >
              {displayCuratedBy}
            </span>
          )}
        </div>

        {displayDescription && (
          <>
            <div className={styles.descriptionContainer}>
              <p
                ref={descriptionRef}
                className={styles.descriptionText}
                translate="no"
              >
                {displayDescription}
              </p>
              {isClamped && (
                <Button
                  variant="ghost"
                  color="gray"
                  size="1"
                  mt="1"
                  onClick={() => setIsDescriptionOpen(true)}
                >
                  Read more
                </Button>
              )}
            </div>

            {isClamped && (
              <DescriptionModal
                title={displayTitle}
                description={displayDescription}
                open={isDescriptionOpen}
                onOpenChange={setIsDescriptionOpen}
              />
            )}
          </>
        )}

        {contentType === 'playlist' && (
          <div className={styles.playlistActionRow}>
            <button
              type="button"
              className={styles.playButton}
              onClick={onPlayAll}
              disabled={tracksCount === 0 || isLoadingTracks}
              aria-label={isCurrentlyPlaying ? 'Pause' : 'Play all tracks'}
            >
              {isCurrentlyPlaying ? (
                <FaPause size={20} />
              ) : (
                <FaPlay size={20} />
              )}
            </button>
            {/* TODO: Temporarily hidden - Save to DISCO button
            <button
              type="button"
              className={styles.saveToDiscoButton}
              onClick={onSaveToDisco}
              disabled={!onSaveToDisco}
              aria-label="Save to DISCO"
              title={!onSaveToDisco ? 'Coming soon' : undefined}
            >
              <Logo size="small" variant="dark" />
              <span>Save to DISCO</span>
            </button>
            */}
          </div>
        )}

        {contentType === 'catalog' && (
          <div className={styles.catalogActionRow}>
            <button
              type="button"
              className={styles.playButton}
              onClick={onPlayAll}
              disabled={tracksCount === 0 || isLoadingTracks}
              aria-label={isCurrentlyPlaying ? 'Pause' : 'Play all tracks'}
            >
              {isCurrentlyPlaying ? (
                <FaPause size={20} />
              ) : (
                <FaPlay size={20} />
              )}
            </button>

            <button
              type="button"
              className={styles.favoriteButton}
              onClick={onFavoriteClick}
              aria-label={
                isFavorite ? 'Remove from favorites' : 'Add to favorites'
              }
            >
              <LuHeart
                size={20}
                className={
                  isFavorite
                    ? styles.favoriteIconFilled
                    : styles.favoriteIconOutline
                }
              />
            </button>

            {/* TODO: Temporarily hidden - License request form button
            {catalogContactDetails && (
              <button
                type="button"
                className={styles.favoriteButton}
                onClick={onContactClick}
                aria-label="License request via email"
              >
                <LuMail size={20} className={styles.favoriteIconOutline} />
              </button>
            )}
            */}

            <button
              type="button"
              className={styles.statusButton}
              onClick={isAccessDisabled ? undefined : onRequestAccess}
              disabled={isAccessDisabled}
              aria-label={getAccessAriaLabel(hasAccess, catalogStatus)}
            >
              {hasAccess ? (
                <LuCircleCheck
                  size={16}
                  className={styles.statusIconApproved}
                />
              ) : (
                <LuLock
                  size={16}
                  className={
                    catalogStatus === 'pending' || catalogStatus === 'invited'
                      ? styles.statusIconPending
                      : styles.statusIconRequest
                  }
                />
              )}
              <span>{getAccessButtonLabel(hasAccess, catalogStatus)}</span>
            </button>

            {catalogUrl && (
              <a
                href={catalogUrl}
                onClick={() => {
                  sendDiscoverMixpanelEvent('Click Button', {
                    copy: 'Visit Catalog',
                    catalogId: contentId,
                    catalogName: displayTitle || 'Untitled Catalog',
                    catalogBusiness: displayCuratedBy || 'Unknown',
                  })
                }}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.visitCatalogButton}
              >
                <span>Visit Catalog</span>
                <LuExternalLink size={16} />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default ContentHero
